import axios from "axios";
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

const API_PREFIX = import.meta.env.VITE_API_URL || "/api/v1";
// Customer JWTs (CUSTOMER scope) have no httpOnly refresh cookie like STAFF
// does — they're issued from /public/auth/set-pin or /verify-pin and last 15
// minutes. Persisting them in sessionStorage means a page refresh / LIFF
// re-mount keeps the customer logged in instead of bouncing them to /login.
// sessionStorage (not localStorage) clears when the tab closes — a sensible
// floor on session lifetime for a public-flow token.
const CUSTOMER_TOKEN_KEY = "tm_customer_token";

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
let onAuthFailure: (() => void) | null = null;

// Restore any cached customer token at module load so the very first request
// from a freshly-mounted CustomerHomePage already has Authorization set.
try {
  const cached = sessionStorage.getItem(CUSTOMER_TOKEN_KEY);
  if (cached) accessToken = cached;
} catch {
  /* sessionStorage may be blocked in some webviews; degrade silently */
}

/**
 * Update the in-memory access token. Pass `persist: 'customer'` to also write
 * to sessionStorage so a page refresh keeps the user signed in. Pass `null`
 * with `persist: 'customer'` to clear the cached customer token (logout).
 */
export function setAccessToken(t: string | null, persist?: "customer") {
  accessToken = t;
  if (persist === "customer") {
    try {
      if (t) sessionStorage.setItem(CUSTOMER_TOKEN_KEY, t);
      else sessionStorage.removeItem(CUSTOMER_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }
}
export function getAccessToken() {
  return accessToken;
}
export function clearCustomerToken() {
  accessToken = null;
  try {
    sessionStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
export function onAuthFailed(fn: () => void) {
  onAuthFailure = fn;
}

export function getErrorMessage(err: unknown, fallback = 'เกิดข้อผิดพลาดในการดำเนินการ'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  const anyErr = err as any;
  const resMsg = anyErr.response?.data?.message;
  if (resMsg) {
    return Array.isArray(resMsg) ? resMsg.join(', ') : String(resMsg);
  }
  if (anyErr.message) return anyErr.message;
  return fallback;
}

export const http: AxiosInstance = axios.create({
  baseURL: API_PREFIX,
  withCredentials: true, // for refresh-token cookie
  headers: { "Content-Type": "application/json" },
});

http.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

http.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    if (!err.response) throw err;
    const original = err.config as AxiosRequestConfig & { __retried?: boolean };
    if (
      err.response.status === 401 &&
      !original.__retried &&
      original.url !== "/auth/refresh"
    ) {
      original.__retried = true;
      
      let isCustomer = false;
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split(".")[1]));
          if (payload.scope === "CUSTOMER") isCustomer = true;
        } catch {
          // ignore
        }
      }

      if (isCustomer) {
        onAuthFailure?.();
      } else {
        try {
          const fresh = await tryRefresh();
          if (fresh) {
            original.headers = {
              ...(original.headers ?? {}),
              Authorization: `Bearer ${fresh}`,
            };
            try {
              return await http.request(original);
            } catch (retryErr: any) {
              if (retryErr.response?.status === 401) {
                onAuthFailure?.();
              }
              throw retryErr;
            }
          }
        } catch {
          // tryRefresh failed or threw
        }
        onAuthFailure?.();
      }
    }
    const serverMessage = (err.response?.data as any)?.message;
    if (serverMessage) {
      err.message = Array.isArray(serverMessage) ? serverMessage.join(', ') : String(serverMessage);
    }
    throw err;
  },
);

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post<{ accessToken: string }>(
        `${API_PREFIX}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      accessToken = r.data.accessToken;
      return accessToken;
    } catch {
      accessToken = null;
      try {
        localStorage.removeItem("tm_has_session");
      } catch {
        /* ignore */
      }
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}
